import { StyledProps } from '@emotion/react';
import styled from '@emotion/styled';
import { getLocaleDirection } from '@utils/locale/direction';
import { AnimationPlaybackControls, useAnimate } from 'framer-motion';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';

import { useProgressDispatchContext } from '../../contexts/ProgressContext';

const AnimatedDiv = styled.div<StyledProps<{ isRtl: boolean }>>`
    height: 100%;
    width: 0%;
    background: white;
    border-radius: '2px';
    position: 'absolute';
    top: 0;
    right: ${(props) => (props.isRtl ? 0 : 'auto')};
    left: ${(props) => (props.isRtl ? 'auto' : 0)};
`;

const ProgressAnimation = () => {
    const dispatch = useProgressDispatchContext();
    const router = useRouter();
    const isRtl = getLocaleDirection(router.locale) === 'rtl' ? true : false;
    const animation = useRef<AnimationPlaybackControls>();
    const [scope, animate] = useAnimate();

    const startAnimation = useCallback(
        (duration?: number, isInfinite = true) => {
            animation.current = animate(
                scope.current,
                { width: '100%' },
                {
                    duration,
                    repeat: isInfinite ? Infinity : 0,
                    // This is where the auto navigation is managed.
                    // We move to the next page as soon as the animation ends,
                    // and the animation only ends if the repeat is set to 0.
                    // This approach is the simplest. This comment is here
                    // because this might not be the most obvious place to manage this behavior.
                    onComplete: () => dispatch({ type: 'NEXT_PAGE' }),
                }
            );
        },
        [scope, animate, dispatch]
    );

    const pauseAnimation = useCallback(() => {
        animation.current?.pause();
    }, []);

    const resumeAnimation = useCallback(() => {
        animation.current?.play();
    }, []);

    const resetAnimation = useCallback(() => {
        animation.current?.cancel();
    }, []);

    useEffect(() => {
        dispatch({
            type: 'SET_ANIMATIONS',
            payload: {
                startAnimation,
                pauseAnimation,
                resumeAnimation,
                resetAnimation,
            },
        });
    }, [
        dispatch,
        startAnimation,
        pauseAnimation,
        resumeAnimation,
        resetAnimation,
    ]);

    return <AnimatedDiv ref={scope} isRtl={isRtl} />;
};

export default ProgressAnimation;
